import React, { useState, useEffect } from 'react';
import {
  Plus,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  Copy,
  Terminal,
  Shield,
  Cloud,
  Loader2,
  Trash2,
  RefreshCw,
  Network,
  Activity,
  ChevronDown,
  ChevronUp,
  Server
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { PaginationControls } from '@/components/ui/pagination-controls';
import {
  apiClient,
  AWSConnection,
  AWSBootstrapResponse,
  AWSVerifyRequest,
  OCIConnection,
  OCIVerifyRequest,
  OCISetupStep,
  OCIRegion,
  cleanErrorMessage,
  AWSAccountFlowLogsStatus,
  OCITenancyFlowLogsStatus,
} from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { useSubscription } from '../hooks/useSubscription';
import { ReactComponent as AWSIcon } from '../assets/icons/aws.svg';
import { ReactComponent as AWSIconDark } from '../assets/icons/aws-dark.svg';
import OCILogo from '../assets/icons/oci.png';
import { ReactComponent as GCPIcon } from '../assets/icons/gcp.svg';
import { GCPCloudTab } from '../components/cloud/GCPCloudTab';
import { ReactComponent as AzureIcon } from '../assets/icons/azure.svg';
import { ReactComponent as VPCIcon } from '../assets/icons/vpc.svg';

// Hook to detect dark mode
function useDarkMode() {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      const isDarkMode = document.documentElement.classList.contains('dark');
      setIsDark(isDarkMode);
    };

    // Check initially
    checkDarkMode();

    // Watch for changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  return isDark;
}

// AWS Icon component that switches based on theme
function AWSIconThemed({ className }: { className?: string }) {
  const isDark = useDarkMode();
  return isDark ? <AWSIconDark className={className} /> : <AWSIcon className={className} />;
}

// OCI Icon component using PNG logo — preserves aspect ratio by only constraining height
function OCIIconThemed({ className }: { className?: string }) {
  return <img src={OCILogo} alt="Oracle Cloud Infrastructure" className={className} style={{ objectFit: 'contain' }} />;
}

export function CloudIntegrations() {
  const { user } = useAuth();
  // VPC Flow Logs power the Incident Response product's network flow
  // analysis; the Workflows product doesn't use them. Only surface the
  // section for tenants with incident response access (trial or paid).
  const { access: productAccess, loading: subscriptionLoading } = useSubscription();
  // Flow logs (AWS VPC / OCI VCN) feed the network analysis of the legacy
  // Incident Response and Cloud AI Copilot products; the Workflows product
  // doesn't use them. Only surface those sections for tenants with access to
  // either product (trial or paid).
  const showFlowLogs = !subscriptionLoading && (productAccess.incident_response || productAccess.copilot);
  const [awsConnections, setAwsConnections] = useState<AWSConnection[]>([]);
  const [ociConnections, setOciConnections] = useState<OCIConnection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isOCILoading, setIsOCILoading] = useState(false);
  const [error, setError] = useState('');
  
  // On-prem mode detection (not actively used since server handles mode switching)
  const [isOnPremMode, setIsOnPremMode] = useState(false);

  // AWS Connection Modal State
  const [showAWSModal, setShowAWSModal] = useState(false);
  const [awsModalStep, setAwsModalStep] = useState<'configure' | 'verify'>('configure');
  const [awsBootstrapData, setAwsBootstrapData] = useState<AWSBootstrapResponse | null>(null);
  const [selectedRegion, setSelectedRegion] = useState('us-east-1');
  const [awsConnectionPurpose, setAwsConnectionPurpose] = useState<'eks' | 'vpc-flow-logs' | 'cloud-ingestion'>('eks');
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [bootstrapError, setBootstrapError] = useState('');
  const [verifyError, setVerifyError] = useState('');
  const [roleArn, setRoleArn] = useState('');
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  // Delete confirmation state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [connectionToDelete, setConnectionToDelete] = useState<AWSConnection | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Account-wide VPC Flow Logs State
  const [accountFlowLogsStatuses, setAccountFlowLogsStatuses] = useState<Map<string, AWSAccountFlowLogsStatus>>(new Map());
  const [loadingAccountFlowLogs, setLoadingAccountFlowLogs] = useState(false);
  const [enablingAccountFlowLogs, setEnablingAccountFlowLogs] = useState<string | null>(null);
  const [disablingAccountFlowLogs, setDisablingAccountFlowLogs] = useState<string | null>(null);
  const [vpcPagination, setVpcPagination] = useState<Map<string, number>>(new Map()); // connection_id -> current page
  const [expandedAccounts, setExpandedAccounts] = useState<Set<string>>(new Set()); // connection_id -> expanded
  const VPC_PAGE_SIZE = 10;

  // OCI Tenancy-wide VCN Flow Logs State
  const [ociFlowLogsStatuses, setOciFlowLogsStatuses] = useState<Map<string, OCITenancyFlowLogsStatus>>(new Map());
  const [loadingOciFlowLogs, setLoadingOciFlowLogs] = useState(false);
  const [enablingOciFlowLogs, setEnablingOciFlowLogs] = useState<string | null>(null);
  const [disablingOciFlowLogs, setDisablingOciFlowLogs] = useState<string | null>(null);
  const [vcnPagination, setVcnPagination] = useState<Map<string, number>>(new Map()); // connection_id -> current page
  const [expandedTenancies, setExpandedTenancies] = useState<Set<string>>(new Set()); // connection_id -> expanded
  const VCN_PAGE_SIZE = 10;

  // OCI Connection Modal State
  const [showOCIModal, setShowOCIModal] = useState(false);
  const [ociSetupSteps, setOciSetupSteps] = useState<OCISetupStep[]>([]);
  const [ociRegions, setOciRegions] = useState<OCIRegion[]>([]);
  const [ociForm, setOciForm] = useState({
    tenancy_ocid: '',
    user_ocid: '',
    fingerprint: '',
    private_key: '',
    region: 'us-ashburn-1',
    connection_name: '',
  });
  const [isOCIVerifying, setIsOCIVerifying] = useState(false);
  const [ociError, setOciError] = useState('');
  const [ociCopySuccess, setOciCopySuccess] = useState<string | null>(null);

  // OCI Delete confirmation state
  const [showOCIDeleteDialog, setShowOCIDeleteDialog] = useState(false);
  const [ociConnectionToDelete, setOciConnectionToDelete] = useState<OCIConnection | null>(null);
  const [isOCIDeleting, setIsOCIDeleting] = useState(false);

  // Check if user is org_admin or ops_admin
  const isAdminOrOps = user?.role === 'org_admin' || user?.role === 'ops_admin';

  // AWS Regions
  const awsRegions = [
    { value: 'us-east-1', label: 'US East (N. Virginia)' },
    { value: 'us-east-2', label: 'US East (Ohio)' },
    { value: 'us-west-1', label: 'US West (N. California)' },
    { value: 'us-west-2', label: 'US West (Oregon)' },
    { value: 'eu-west-1', label: 'Europe (Ireland)' },
    { value: 'eu-west-2', label: 'Europe (London)' },
    { value: 'eu-central-1', label: 'Europe (Frankfurt)' },
    { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
    { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
    { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
  ];

  useEffect(() => {
    // Check if running in on-prem mode
    apiClient.getHealthStatus()
      .then((health) => {
        setIsOnPremMode(health.is_onprem_mode || false);
      })
      .catch(() => {
        // If health check fails, assume not on-prem
        setIsOnPremMode(false);
      });
  }, []);

  useEffect(() => {
    if (isAdminOrOps) {
      loadAWSConnections();
      loadOCIConnections();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdminOrOps]);

  const loadAWSConnections = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await apiClient.getAWSConnectionStatus();
      const connections = response.connections || [];
      setAwsConnections(connections);
      // Also load account-wide VPC Flow Logs statuses
      if (connections.length > 0) {
        loadAccountFlowLogsStatuses(connections);
      }
    } catch (err: any) {
      setError(cleanErrorMessage(err) || 'Failed to load AWS connections');
    } finally {
      setIsLoading(false);
    }
  };

  // Load account-wide VPC Flow Logs status for all VPC Flow Logs connections
  // Uses Promise.allSettled for parallel requests with graceful error handling
  const loadAccountFlowLogsStatuses = async (connections: AWSConnection[]) => {
    setLoadingAccountFlowLogs(true);
    const statusMap = new Map<string, AWSAccountFlowLogsStatus>();

    // Get all connections with VPC Flow Logs capability
    const vpcFlowLogsConnections = connections.filter(conn => {
      const capabilities = getConnectionCapabilities(conn);
      return conn.connection_status === 'connected' && capabilities.includes('vpc-flow-logs');
    });

    // Fetch all statuses in parallel
    const results = await Promise.allSettled(
      vpcFlowLogsConnections.map(conn =>
        apiClient.getAWSAccountFlowLogsStatus(conn.id)
          .then(status => ({ conn, status, success: true as const }))
          .catch(err => {
            console.error(`Failed to load account flow logs status for ${conn.id}:`, err);
            return { conn, status: null, success: false as const };
          })
      )
    );

    // Process results
    results.forEach(result => {
      if (result.status === 'fulfilled') {
        const { conn, status, success } = result.value;
        statusMap.set(conn.id, success && status ? status : {
          aws_account_id: conn.account_id,
          enabled: false,
          vpc_count: 0,
          vpcs: []
        });
      }
    });

    setAccountFlowLogsStatuses(statusMap);
    setLoadingAccountFlowLogs(false);
  };

  const handleEnableAccountFlowLogs = async (connectionId: string) => {
    try {
      setEnablingAccountFlowLogs(connectionId);
      setError('');
      await apiClient.enableAWSAccountFlowLogs(connectionId);
      // Reload the status after enabling
      const status = await apiClient.getAWSAccountFlowLogsStatus(connectionId);
      setAccountFlowLogsStatuses(prev => {
        const newMap = new Map(prev);
        newMap.set(connectionId, status);
        return newMap;
      });
    } catch (err: any) {
      setError(cleanErrorMessage(err) || 'Failed to enable VPC Flow Logs');
    } finally {
      setEnablingAccountFlowLogs(null);
    }
  };

  const handleDisableAccountFlowLogs = async (connectionId: string) => {
    try {
      setDisablingAccountFlowLogs(connectionId);
      setError('');
      await apiClient.disableAWSAccountFlowLogs(connectionId);
      // Reload the status after disabling
      const status = await apiClient.getAWSAccountFlowLogsStatus(connectionId);
      setAccountFlowLogsStatuses(prev => {
        const newMap = new Map(prev);
        newMap.set(connectionId, status);
        return newMap;
      });
    } catch (err: any) {
      setError(cleanErrorMessage(err) || 'Failed to disable VPC Flow Logs');
    } finally {
      setDisablingAccountFlowLogs(null);
    }
  };

  // OCI VCN Flow Logs handlers
  const loadOCIFlowLogsStatuses = async (connections: OCIConnection[]) => {
    if (connections.length === 0) return;
    try {
      setLoadingOciFlowLogs(true);
      const statuses = await Promise.all(
        connections.map(async (conn) => {
          try {
            const status = await apiClient.getOCITenancyFlowLogsStatus(conn.id);
            return { connectionId: conn.id, status };
          } catch {
            return { connectionId: conn.id, status: null };
          }
        })
      );
      const newMap = new Map<string, OCITenancyFlowLogsStatus>();
      statuses.forEach(({ connectionId, status }) => {
        if (status) {
          newMap.set(connectionId, status);
        }
      });
      setOciFlowLogsStatuses(newMap);
    } finally {
      setLoadingOciFlowLogs(false);
    }
  };

  const handleEnableOciFlowLogs = async (connectionId: string) => {
    try {
      setEnablingOciFlowLogs(connectionId);
      setOciError('');
      await apiClient.enableOCITenancyFlowLogs(connectionId);
      // Reload the status after enabling
      const status = await apiClient.getOCITenancyFlowLogsStatus(connectionId);
      setOciFlowLogsStatuses(prev => {
        const newMap = new Map(prev);
        newMap.set(connectionId, status);
        return newMap;
      });
    } catch (err: any) {
      setOciError(cleanErrorMessage(err) || 'Failed to enable VCN Flow Logs');
    } finally {
      setEnablingOciFlowLogs(null);
    }
  };

  const handleDisableOciFlowLogs = async (connectionId: string) => {
    try {
      setDisablingOciFlowLogs(connectionId);
      setOciError('');
      await apiClient.disableOCITenancyFlowLogs(connectionId);
      // Reload the status after disabling
      const status = await apiClient.getOCITenancyFlowLogsStatus(connectionId);
      setOciFlowLogsStatuses(prev => {
        const newMap = new Map(prev);
        newMap.set(connectionId, status);
        return newMap;
      });
    } catch (err: any) {
      setOciError(cleanErrorMessage(err) || 'Failed to disable VCN Flow Logs');
    } finally {
      setDisablingOciFlowLogs(null);
    }
  };

  const loadOCIConnections = async () => {
    try {
      setIsOCILoading(true);
      const response = await apiClient.getOCIConnectionStatus();
      const connections = response.connections || [];
      setOciConnections(connections);
      // Also load tenancy-wide VCN Flow Logs statuses
      if (connections.length > 0) {
        loadOCIFlowLogsStatuses(connections.filter(c => c.connection_status === 'connected'));
      }
    } catch (err: any) {
      console.error('Failed to load OCI connections:', err);
    } finally {
      setIsOCILoading(false);
    }
  };

  const loadOCISetupInstructions = async () => {
    try {
      const response = await apiClient.getOCISetupInstructions();
      setOciSetupSteps(response.steps || []);
      setOciRegions(response.regions || []);
    } catch (err: any) {
      console.error('Failed to load OCI setup instructions:', err);
    }
  };

  const handleOpenOCIModal = () => {
    loadOCISetupInstructions();
    setShowOCIModal(true);
  };

  const handleCloseOCIModal = () => {
    setShowOCIModal(false);
    setOciForm({
      tenancy_ocid: '',
      user_ocid: '',
      fingerprint: '',
      private_key: '',
      region: 'us-ashburn-1',
      connection_name: '',
    });
    setOciError('');
    setOciCopySuccess(null);
  };

  const handleOCIVerify = async () => {
    if (!ociForm.tenancy_ocid.trim() || !ociForm.user_ocid.trim() || 
        !ociForm.fingerprint.trim() || !ociForm.private_key.trim()) {
      setOciError('All fields except Connection Name are required');
      return;
    }

    try {
      setIsOCIVerifying(true);
      setOciError('');

      const request: OCIVerifyRequest = {
        tenancy_ocid: ociForm.tenancy_ocid.trim(),
        user_ocid: ociForm.user_ocid.trim(),
        fingerprint: ociForm.fingerprint.trim(),
        private_key: ociForm.private_key.trim(),
        region: ociForm.region,
        connection_name: ociForm.connection_name.trim() || undefined,
      };

      const response = await apiClient.verifyOCICredentials(request);

      if (response.status === 'connected') {
        handleCloseOCIModal();
        await loadOCIConnections();
      } else {
        setOciError(response.message || 'Failed to verify OCI credentials');
      }
    } catch (err: any) {
      setOciError(cleanErrorMessage(err) || 'Failed to verify OCI credentials');
    } finally {
      setIsOCIVerifying(false);
    }
  };

  const handleDeleteOCIConnection = async () => {
    if (!ociConnectionToDelete) return;

    try {
      setIsOCIDeleting(true);
      await apiClient.deleteOCIConnection(ociConnectionToDelete.id);
      setShowOCIDeleteDialog(false);
      setOciConnectionToDelete(null);
      await loadOCIConnections();
    } catch (err: any) {
      setError(cleanErrorMessage(err) || 'Failed to delete OCI connection');
    } finally {
      setIsOCIDeleting(false);
    }
  };

  const copyOCIToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setOciCopySuccess(type);
      setTimeout(() => setOciCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleAWSBootstrap = async () => {
    try {
      setIsBootstrapping(true);
      setBootstrapError('');

      if (awsConnectionPurpose === 'eks') {
        // EKS provisioning - use backend API
        const response = await apiClient.bootstrapAWSIntegration({ region: selectedRegion });
        setAwsBootstrapData(response);
        setAwsModalStep('verify');
      } else if (awsConnectionPurpose === 'vpc-flow-logs') {
        // VPC Flow Logs - server handles on-prem vs SaaS template selection
        const response = await apiClient.getVPCFlowLogsTemplate(selectedRegion);
        setAwsBootstrapData(response);
        setAwsModalStep('verify');
      } else {
        // Cloud Ingestion - server handles on-prem vs SaaS template selection
        const response = await apiClient.getCloudIngestionTemplate(selectedRegion);
        setAwsBootstrapData(response);
        setAwsModalStep('verify');
      }
    } catch (err: any) {
      setBootstrapError(cleanErrorMessage(err) || 'Failed to bootstrap AWS integration');
    } finally {
      setIsBootstrapping(false);
    }
  };

  const handleAWSVerify = async () => {
    if (!awsBootstrapData || !roleArn.trim()) {
      setVerifyError('Role ARN is required');
      return;
    }

    try {
      setIsVerifying(true);
      setVerifyError('');
      
      const request: AWSVerifyRequest = {
        role_arn: roleArn.trim(),
        external_id: awsBootstrapData.external_id,
        region: selectedRegion,
      };

      const response = await apiClient.verifyAWSRole(request);
      
      if (response.status === 'connected') {
        // Success! Close modal and refresh connections
        handleCloseAWSModal();
        await loadAWSConnections();
      } else {
        setVerifyError(response.message || 'Failed to verify AWS role');
      }
    } catch (err: any) {
      setVerifyError(cleanErrorMessage(err) || 'Failed to verify AWS role');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCloseAWSModal = () => {
    setShowAWSModal(false);
    setAwsModalStep('configure');
    setAwsBootstrapData(null);
    setSelectedRegion('us-east-1');
    setAwsConnectionPurpose('eks');
    setRoleArn('');
    setBootstrapError('');
    setVerifyError('');
    setCopySuccess(null);
  };

  const handleOpenAWSModal = (purpose: 'eks' | 'vpc-flow-logs' | 'cloud-ingestion') => {
    setAwsConnectionPurpose(purpose);
    setShowAWSModal(true);
  };

  const handleDeleteConnection = async () => {
    if (!connectionToDelete) return;

    try {
      setIsDeleting(true);
      await apiClient.deleteAWSConnection(connectionToDelete.id);
      setShowDeleteDialog(false);
      setConnectionToDelete(null);
      await loadAWSConnections();
    } catch (err: any) {
      setError(cleanErrorMessage(err) || 'Failed to delete AWS connection');
    } finally {
      setIsDeleting(false);
    }
  };

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(type);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const getConnectionStatusBadge = (status: string) => {
    switch (status) {
      case 'connected':
        return (
          <Badge variant="default" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 font-normal text-xs">
            Connected
          </Badge>
        );
      case 'failed':
        return (
          <Badge variant="default" className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 font-normal text-xs">
            Failed
          </Badge>
        );
      case 'pending':
        return (
          <Badge variant="secondary" className="font-normal text-xs">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Pending
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary" className="font-normal text-xs">
            {status}
          </Badge>
        );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getConnectionCapabilities = (connection: AWSConnection): ('eks' | 'vpc-flow-logs' | 'cloud-ingestion')[] => {
    // Determine what this AWS connection can do based on role name
    const capabilities: ('eks' | 'vpc-flow-logs' | 'cloud-ingestion')[] = [];

    if (connection.role_arn.includes('VPCFlowLogs')) {
      capabilities.push('vpc-flow-logs');
    } else if (connection.role_arn.includes('CloudIngestion')) {
      // Both SaaS and on-prem use Kestrel-CloudIngestionRole
      capabilities.push('cloud-ingestion');
    } else if (connection.role_arn.includes('Provisioning')) {
      capabilities.push('eks');
    } else {
      // If role name doesn't match our patterns, assume it's EKS (legacy)
      capabilities.push('eks');
    }

    return capabilities;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAdminOrOps) {
    return (
      <div className="space-y-6">
        <Alert>
          <AlertDescription>
            You must be an organization administrator to manage cloud integrations.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Error Display */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Tabs defaultValue="aws">
          <TabsList className="justify-start h-auto p-1 gap-1 flex-wrap">
            <TabsTrigger value="aws" className="flex items-center gap-2 px-4 py-2.5">
              <AWSIconThemed className="h-5 w-5" />
              <span>Amazon Web Services</span>
            </TabsTrigger>
            <TabsTrigger value="gcp" className="flex items-center gap-2 px-4 py-2.5">
              <GCPIcon className="h-5 w-5" />
              <span>Google Cloud</span>
            </TabsTrigger>
            <TabsTrigger value="oci" className="flex items-center gap-2 px-4 py-2.5">
              <OCIIconThemed className="h-5 w-5" />
              <span>Oracle Cloud</span>
            </TabsTrigger>
            <TabsTrigger value="azure" disabled className="flex items-center gap-2 px-4 py-2.5 opacity-50">
              <AzureIcon className="h-5 w-5" />
              <span>Microsoft Azure</span>
              {/* Azure is unbuilt rather than paywalled: the tab is disabled for
                  everyone, so "Paid" would wrongly imply upgrading unlocks it. */}
              <Badge variant="outline" className="text-xs ml-1">Soon</Badge>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="aws" className="mt-4">
        {/* AWS Parent Section */}
        <div className="space-y-6 p-6 border-2 border-border rounded-lg bg-card">
          {/* AWS Header */}
          <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b">
            <div className="flex items-center gap-3">
              <AWSIconThemed className="h-8 w-8" />
              <div>
                <h2 className="text-2xl font-bold">Amazon Web Services</h2>
                <p className="text-sm text-muted-foreground">
                  Manage AWS accounts for cost, compute, security, and infrastructure automation
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={() => { loadAWSConnections(); }} disabled={isLoading}>
              <RefreshCw className={cn("h-4 w-4 mr-2", (isLoading || loadingAccountFlowLogs) && "animate-spin")} />
              Refresh
            </Button>
          </div>

          {/* Connected Accounts Subsection */}
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <CardTitle>Connected Accounts</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Connected accounts unlock the AWS trigger and action blocks in Workflows
                  </p>
                </div>
                {awsConnections.filter(conn => {
                  const capabilities = getConnectionCapabilities(conn);
                  return conn.connection_status === 'connected' && capabilities.includes('cloud-ingestion');
                }).length > 0 && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => handleOpenAWSModal('cloud-ingestion')} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Connect AWS Account
                  </Button>
                </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {awsConnections.filter(conn => {
                const capabilities = getConnectionCapabilities(conn);
                return conn.connection_status === 'connected' && capabilities.includes('cloud-ingestion');
              }              ).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <p className="text-sm text-muted-foreground mb-4">
                    No AWS accounts connected. Connect an account to unlock the AWS trigger and action blocks in Workflows.
                  </p>
                  <Button variant="outline" className="bg-white text-black hover:bg-white/90 dark:bg-white dark:text-black dark:hover:bg-white/90" onClick={() => handleOpenAWSModal('cloud-ingestion')}>
                    <Plus className="h-4 w-4 mr-2" />
                    Connect AWS Account
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-muted/50 p-4 rounded-lg border mb-4">
                    <p className="text-sm text-muted-foreground">
                      Connecting an account gives Kestrel least-privilege access via an IAM role — no keys are stored. It unlocks the AWS trigger and action blocks in Workflows and keeps an inventory of your resources for investigations.
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Account ID</TableHead>
                        <TableHead>Region</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Connected</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {awsConnections
                        .filter(conn => {
                          const capabilities = getConnectionCapabilities(conn);
                          return capabilities.includes('cloud-ingestion');
                        })
                        .map((connection) => (
                          <TableRow key={connection.id}>
                            <TableCell className="font-mono text-sm">
                              {connection.account_id}
                            </TableCell>
                            <TableCell>{connection.region}</TableCell>
                            <TableCell>
                              {getConnectionStatusBadge(connection.connection_status)}
                            </TableCell>
                            <TableCell>{formatDate(connection.created_at)}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => {
                                  setConnectionToDelete(connection);
                                  setShowDeleteDialog(true);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      }
                    </TableBody>
                  </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Account-wide VPC Flow Logs Section — Incident Response only */}
          {showFlowLogs && (
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <CardTitle>Account-wide VPC Flow Logs</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    VPC Flow Logs are automatically enabled for VPCs with resources
                  </p>
                </div>
                {awsConnections.some(conn => conn.connection_status === 'connected' && getConnectionCapabilities(conn).includes('vpc-flow-logs')) && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" onClick={() => handleOpenAWSModal('vpc-flow-logs')} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add VPC Connection
                  </Button>
                </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
                {loadingAccountFlowLogs ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" />
                    <span>Loading VPC Flow Logs status...</span>
                  </div>
                ) : awsConnections.filter(conn => {
                    const capabilities = getConnectionCapabilities(conn);
                    return conn.connection_status === 'connected' && capabilities.includes('vpc-flow-logs');
                  }).length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <p className="text-sm text-muted-foreground mb-4">
                      No VPC flow log connections. Connect your AWS account to automatically enable VPC Flow Logs.
                    </p>
                    <Button variant="outline" className="bg-white text-black hover:bg-white/90 dark:bg-white dark:text-black dark:hover:bg-white/90" onClick={() => handleOpenAWSModal('vpc-flow-logs')}>
                      <Plus className="h-4 w-4 mr-2" />
                      Add VPC Connection
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {awsConnections
                      .filter(conn => {
                        const capabilities = getConnectionCapabilities(conn);
                        return conn.connection_status === 'connected' && capabilities.includes('vpc-flow-logs');
                      })
                      .map((connection) => {
                        const status = accountFlowLogsStatuses.get(connection.id);
                        const vpcs = status?.vpcs ?? [];
                        const enabledCount = vpcs.filter(v => v.enabled).length;
                        const totalCount = vpcs.length;
                        const isExpanded = expandedAccounts.has(connection.id);

                        const toggleExpanded = () => {
                          setExpandedAccounts(prev => {
                            const newSet = new Set(prev);
                            if (newSet.has(connection.id)) {
                              newSet.delete(connection.id);
                            } else {
                              newSet.add(connection.id);
                            }
                            return newSet;
                          });
                        };

                        return (
                          <div key={connection.id} className="border rounded-lg overflow-hidden">
                            {/* Collapsible Header */}
                            <div
                              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                              onClick={toggleExpanded}
                            >
                              <div className="flex items-center gap-3 min-w-0">
                                <Button variant="ghost" size="sm" className="p-0 h-6 w-6 flex-shrink-0">
                                  {isExpanded ? (
                                    <ChevronUp className="h-4 w-4" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" />
                                  )}
                                </Button>
                                <div className="min-w-0">
                                  <h4 className="font-medium flex flex-wrap items-center gap-2">
                                    Account: <code className="text-sm bg-muted px-2 py-0.5 rounded break-all">{connection.account_id}</code>
                                  </h4>
                                  <p className="text-sm text-muted-foreground">
                                    {enabledCount} of {totalCount} VPCs with resources have flow logs enabled
                                  </p>
                                </div>
                              </div>
                              <div className="flex flex-wrap items-center gap-2 ml-9 sm:ml-0 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                {enabledCount > 0 ? (
                                  <>
                                    <Badge variant="outline" className="text-xs font-normal">
                                      <CheckCircle className="h-3 w-3 mr-1" />
                                      Auto-managed
                                    </Badge>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                      onClick={() => handleDisableAccountFlowLogs(connection.id)}
                                      disabled={disablingAccountFlowLogs === connection.id}
                                    >
                                      {disablingAccountFlowLogs === connection.id ? (
                                        <>
                                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                          Disabling...
                                        </>
                                      ) : (
                                        <>
                                          <Trash2 className="h-4 w-4 mr-2" />
                                          Disable All
                                        </>
                                      )}
                                    </Button>
                                  </>
                                ) : totalCount > 0 ? (
                                  <>
                                    <Badge variant="outline" className="text-muted-foreground">
                                      Disabled
                                    </Badge>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={() => handleEnableAccountFlowLogs(connection.id)}
                                      disabled={enablingAccountFlowLogs === connection.id}
                                    >
                                      {enablingAccountFlowLogs === connection.id ? (
                                        <>
                                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                          Enabling...
                                        </>
                                      ) : (
                                        <>
                                          <Plus className="h-4 w-4 mr-2" />
                                          Enable All
                                        </>
                                      )}
                                    </Button>
                                  </>
                                ) : (
                                  <Badge variant="outline" className="text-muted-foreground">
                                    Waiting for resource sync
                                  </Badge>
                                )}
                              </div>
                            </div>

                            {/* Collapsible Content */}
                            {isExpanded && (
                              <div className="border-t p-4 space-y-4">
                                {/* Auto-enable info box */}
                                <div className="bg-muted/50 p-3 rounded-lg border">
                                  <p className="text-sm text-muted-foreground">
                                    VPC Flow Logs are automatically enabled for VPCs that have traffic-generating resources (EC2, RDS, Lambda, ECS, EKS, etc.).
                                    New VPCs with resources are automatically detected every 10 minutes.
                                  </p>
                                </div>

                            {status?.vpcs && status.vpcs.length > 0 ? (
                              (() => {
                                const maxVpcPage = Math.max(0, Math.ceil(status.vpcs.length / VPC_PAGE_SIZE) - 1);
                                const currentPage = Math.min(vpcPagination.get(connection.id) || 0, maxVpcPage);
                                const paginatedVpcs = status.vpcs.slice(
                                  currentPage * VPC_PAGE_SIZE,
                                  (currentPage + 1) * VPC_PAGE_SIZE
                                );

                                return (
                                  <>
                                    <div className="overflow-x-auto">
                                    <Table>
                                      <TableHeader>
                                        <TableRow>
                                          <TableHead>VPC ID</TableHead>
                                          <TableHead>Region</TableHead>
                                          <TableHead>Resources</TableHead>
                                          <TableHead>Status</TableHead>
                                          <TableHead>Flow Log ID</TableHead>
                                          <TableHead>Last Polled</TableHead>
                                          <TableHead>Last Processed</TableHead>
                                        </TableRow>
                                      </TableHeader>
                                      <TableBody>
                                        {paginatedVpcs.map((vpc) => (
                                          <TableRow key={vpc.vpc_id}>
                                            <TableCell>
                                              <code className="text-xs font-mono bg-muted px-2 py-1 rounded">
                                                {vpc.vpc_id}
                                              </code>
                                            </TableCell>
                                            <TableCell>
                                              <span className="text-xs text-muted-foreground">
                                                {vpc.region || '-'}
                                              </span>
                                            </TableCell>
                                            <TableCell>
                                              <Badge variant="outline" className="text-xs">
                                                {vpc.resource_count || 0} resources
                                              </Badge>
                                            </TableCell>
                                            <TableCell>
                                              {vpc.enabled ? (
                                                <Badge variant="outline" className="text-xs font-normal">
                                                  Enabled
                                                </Badge>
                                              ) : vpc.error_message ? (
                                                <Tooltip>
                                                  <TooltipTrigger asChild>
                                                    <Badge variant="outline" className="text-xs font-normal text-destructive cursor-help">
                                                      Error
                                                    </Badge>
                                                  </TooltipTrigger>
                                                  <TooltipContent className="max-w-xs">
                                                    <p className="text-xs">{vpc.error_message}</p>
                                                  </TooltipContent>
                                                </Tooltip>
                                              ) : (
                                                <Badge variant="outline" className="text-gray-600">
                                                  Disabled
                                                </Badge>
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              {vpc.flow_log_id ? (
                                                <code className="text-xs font-mono bg-muted px-2 py-1 rounded">
                                                  {vpc.flow_log_id}
                                                </code>
                                              ) : (
                                                <span className="text-muted-foreground text-sm">-</span>
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              {vpc.last_poll_time ? (
                                                <span className="text-xs text-muted-foreground">
                                                  {new Date(vpc.last_poll_time).toLocaleString()}
                                                </span>
                                              ) : vpc.enabled ? (
                                                <Tooltip>
                                                  <TooltipTrigger asChild>
                                                    <span className="text-xs text-muted-foreground cursor-help">
                                                        Initializing...
                                                    </span>
                                                  </TooltipTrigger>
                                                  <TooltipContent className="max-w-xs">
                                                    <p className="text-xs">Waiting for AWS to create CloudWatch log group. This can take 5-15 minutes.</p>
                                                  </TooltipContent>
                                                </Tooltip>
                                              ) : (
                                                <span className="text-muted-foreground text-sm">-</span>
                                              )}
                                            </TableCell>
                                            <TableCell>
                                              {vpc.last_processed_timestamp ? (
                                                <span className="text-xs text-muted-foreground">
                                                  {new Date(vpc.last_processed_timestamp).toLocaleString()}
                                                </span>
                                              ) : vpc.enabled ? (
                                                <Tooltip>
                                                  <TooltipTrigger asChild>
                                                    <span className="text-xs text-muted-foreground cursor-help">
                                                        Waiting for logs...
                                                    </span>
                                                  </TooltipTrigger>
                                                  <TooltipContent className="max-w-xs">
                                                    <p className="text-xs">No logs processed yet. AWS VPC Flow Logs can take 5-15 minutes to start.</p>
                                                  </TooltipContent>
                                                </Tooltip>
                                              ) : (
                                                <span className="text-muted-foreground text-sm">-</span>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                    </div>

                                    {/* Pagination */}
                                    <PaginationControls
                                      page={currentPage}
                                      pageSize={VPC_PAGE_SIZE}
                                      totalItems={status.vpcs.length}
                                      onPageChange={(newPage) => {
                                        setVpcPagination(prev => {
                                          const newMap = new Map(prev);
                                          newMap.set(connection.id, newPage);
                                          return newMap;
                                        });
                                      }}
                                      itemLabel="VPCs"
                                    />
                                  </>
                                );
                              })()
                            ) : (
                              <div className="text-center py-6 text-muted-foreground">
                                <p className="text-sm">No VPCs with resources discovered yet.</p>
                                <p className="text-xs mt-1">VPCs with traffic-generating resources will appear here automatically within a few minutes.</p>
                              </div>
                            )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
        {/* End AWS Parent Section */}
          </TabsContent>

          <TabsContent value="oci" className="mt-4">
        {/* OCI Parent Section */}
        <div className="space-y-6 p-6 border-2 border-border rounded-lg bg-card">
          {/* OCI Header */}
          <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b">
            <div className="flex items-center gap-3">
              <OCIIconThemed className="h-8 w-8" />
              <div>
                <h2 className="text-2xl font-bold">Oracle Cloud Infrastructure</h2>
                <p className="text-sm text-muted-foreground">
                  Manage OCI tenancies for resource discovery and inventory
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={loadOCIConnections} disabled={isOCILoading}>
              <RefreshCw className={cn("h-4 w-4 mr-2", (isOCILoading || loadingOciFlowLogs) && "animate-spin")} />
              Refresh
            </Button>
          </div>

          {/* OCI Connected Tenancies */}
          <Card>
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <CardTitle>Connected Tenancies</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Connect your Oracle Cloud tenancy for resource discovery and inventory
                  </p>
                </div>
                {ociConnections.length > 0 && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={handleOpenOCIModal} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Add OCI Connection
                  </Button>
                </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {ociConnections.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <p className="text-sm text-muted-foreground mb-4">
                    No OCI tenancies connected. Connect your Oracle Cloud tenancy to give Kestrel read-only access to your resources.
                  </p>
                  <Button variant="outline" className="bg-white text-black hover:bg-white/90 dark:bg-white dark:text-black dark:hover:bg-white/90" onClick={handleOpenOCIModal}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add OCI Connection
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-muted/50 p-4 rounded-lg border mb-4">
                    <p className="text-sm text-muted-foreground">
                      Connecting a tenancy lets Kestrel discover and catalog your Oracle Cloud resources including Compute instances,
                      OKE clusters, Autonomous Databases, Object Storage, and more. This data powers Kestrel's cloud incident response and AI copilot.
                    </p>
                  </div>

                  <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tenancy</TableHead>
                        <TableHead>Region</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Connected</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ociConnections.map((connection) => (
                        <TableRow key={connection.id}>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-mono text-sm truncate max-w-[200px]" title={connection.tenancy_ocid}>
                                {connection.connection_name || connection.tenancy_ocid.split('.').pop()?.substring(0, 12) + '...'}
                              </span>
                              {connection.connection_name && (
                                <span className="text-xs text-muted-foreground truncate max-w-[200px]" title={connection.tenancy_ocid}>
                                  {connection.tenancy_ocid.split('.').pop()?.substring(0, 12)}...
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{connection.region}</TableCell>
                          <TableCell>
                            {getConnectionStatusBadge(connection.connection_status)}
                          </TableCell>
                          <TableCell>{formatDate(connection.created_at)}</TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => {
                                setOciConnectionToDelete(connection);
                                setShowOCIDeleteDialog(true);
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tenancy-wide VCN Flow Logs Section — legacy products only */}
          {showFlowLogs && (
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="min-w-0">
                  <CardTitle>Tenancy-wide VCN Flow Logs</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    VCN Flow Logs are automatically enabled for VCNs with resources
                  </p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingOciFlowLogs ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  <span>Loading VCN Flow Logs status...</span>
                </div>
              ) : ociConnections.filter(c => c.connection_status === 'connected').length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <p className="text-sm text-muted-foreground mb-4">
                    No OCI connections. Connect your tenancy to automatically enable VCN Flow Logs.
                  </p>
                  <Button variant="outline" className="bg-white text-black hover:bg-white/90 dark:bg-white dark:text-black dark:hover:bg-white/90" onClick={() => setShowOCIModal(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Connect OCI Tenancy
                  </Button>
                </div>
              ) : (
                <div className="space-y-6">
                  {ociConnections
                    .filter(c => c.connection_status === 'connected')
                    .map((connection) => {
                      const status = ociFlowLogsStatuses.get(connection.id);
                      const vcns = status?.vcns ?? [];
                      const enabledCount = vcns.filter(v => v.enabled).length;
                      const totalCount = vcns.length;
                      const isExpanded = expandedTenancies.has(connection.id);

                      const toggleExpanded = () => {
                        setExpandedTenancies(prev => {
                          const newSet = new Set(prev);
                          if (newSet.has(connection.id)) {
                            newSet.delete(connection.id);
                          } else {
                            newSet.add(connection.id);
                          }
                          return newSet;
                        });
                      };

                      return (
                        <div key={connection.id} className="border rounded-lg overflow-hidden">
                          {/* Collapsible Header */}
                          <div
                            className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 cursor-pointer hover:bg-muted/50 transition-colors"
                            onClick={toggleExpanded}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <Button variant="ghost" size="sm" className="p-0 h-6 w-6 flex-shrink-0">
                                {isExpanded ? (
                                  <ChevronUp className="h-4 w-4" />
                                ) : (
                                  <ChevronDown className="h-4 w-4" />
                                )}
                              </Button>
                              <div className="min-w-0">
                                <h4 className="font-medium flex flex-wrap items-center gap-2">
                                  Tenancy: <code className="text-sm bg-muted px-2 py-0.5 rounded break-all">{connection.connection_name || connection.tenancy_ocid}</code>
                                </h4>
                                <p className="text-sm text-muted-foreground">
                                  {enabledCount} of {totalCount} VCNs with resources have flow logs enabled
                                </p>
                              </div>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 ml-9 sm:ml-0 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                              {enabledCount > 0 ? (
                                <>
                                  <Badge variant="outline" className="text-xs font-normal">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Auto-managed
                                  </Badge>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                    onClick={() => handleDisableOciFlowLogs(connection.id)}
                                    disabled={disablingOciFlowLogs === connection.id}
                                  >
                                    {disablingOciFlowLogs === connection.id ? (
                                      <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Disabling...
                                      </>
                                    ) : (
                                      <>
                                        <Trash2 className="h-4 w-4 mr-2" />
                                        Disable All
                                      </>
                                    )}
                                  </Button>
                                </>
                              ) : totalCount > 0 ? (
                                <>
                                  <Badge variant="outline" className="text-muted-foreground">
                                    Disabled
                                  </Badge>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleEnableOciFlowLogs(connection.id)}
                                    disabled={enablingOciFlowLogs === connection.id}
                                  >
                                    {enablingOciFlowLogs === connection.id ? (
                                      <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Enabling...
                                      </>
                                    ) : (
                                      <>
                                        <Plus className="h-4 w-4 mr-2" />
                                        Enable All
                                      </>
                                    )}
                                  </Button>
                                </>
                              ) : (
                                <Badge variant="outline" className="text-muted-foreground">
                                  Waiting for resource sync
                                </Badge>
                              )}
                            </div>
                          </div>

                          {/* Collapsible Content */}
                          {isExpanded && (
                            <div className="border-t p-4 space-y-4">
                              {/* Auto-enable info box */}
                              <div className="bg-muted/50 p-3 rounded-lg border">
                                <p className="text-sm text-muted-foreground">
                                  VCN Flow Logs are automatically enabled for VCNs that have traffic-generating resources (Compute, Database, Load Balancer, etc.).
                                  New VCNs with resources are automatically detected every 10 minutes.
                                </p>
                              </div>

                              {status?.vcns && status.vcns.length > 0 ? (
                                (() => {
                                  const maxVcnPage = Math.max(0, Math.ceil(status.vcns.length / VCN_PAGE_SIZE) - 1);
                                  const currentPage = Math.min(vcnPagination.get(connection.id) || 0, maxVcnPage);
                                  const paginatedVcns = status.vcns.slice(
                                    currentPage * VCN_PAGE_SIZE,
                                    (currentPage + 1) * VCN_PAGE_SIZE
                                  );

                                  return (
                                    <>
                                      <div className="overflow-x-auto">
                                      <Table>
                                        <TableHeader>
                                          <TableRow>
                                            <TableHead>VCN Name</TableHead>
                                            <TableHead>Region</TableHead>
                                            <TableHead>Resources</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead>Log OCID</TableHead>
                                            <TableHead>Last Polled</TableHead>
                                            <TableHead>Last Processed</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {paginatedVcns.map((vcn) => (
                                            <TableRow key={vcn.vcn_id}>
                                              <TableCell>
                                                <div className="flex flex-col">
                                                  <span className="font-medium">{vcn.vcn_name || 'Unnamed VCN'}</span>
                                                  <code className="text-xs font-mono text-muted-foreground">
                                                    {vcn.vcn_id.length > 40 ? `${vcn.vcn_id.substring(0, 40)}...` : vcn.vcn_id}
                                                  </code>
                                                </div>
                                              </TableCell>
                                              <TableCell>
                                                <span className="text-xs text-muted-foreground">
                                                  {vcn.region || '-'}
                                                </span>
                                              </TableCell>
                                              <TableCell>
                                                <Badge variant="outline" className="text-xs">
                                                  {vcn.resource_count || 0} resources
                                                </Badge>
                                              </TableCell>
                                              <TableCell>
                                                {vcn.enabled ? (
                                                  <Badge variant="outline" className="text-xs font-normal">
                                                    Enabled
                                                  </Badge>
                                                ) : vcn.error_message ? (
                                                  <Tooltip>
                                                    <TooltipTrigger asChild>
                                                      <Badge variant="outline" className="text-xs font-normal text-destructive cursor-help">
                                                        Error
                                                      </Badge>
                                                    </TooltipTrigger>
                                                    <TooltipContent className="max-w-xs">
                                                      <p className="text-xs">{vcn.error_message}</p>
                                                    </TooltipContent>
                                                  </Tooltip>
                                                ) : (
                                                  <Badge variant="outline" className="text-gray-600">
                                                    Disabled
                                                  </Badge>
                                                )}
                                              </TableCell>
                                              <TableCell>
                                                {vcn.log_ocid ? (
                                                  <Tooltip>
                                                    <TooltipTrigger asChild>
                                                      <code className="text-xs font-mono bg-muted px-2 py-1 rounded cursor-help">
                                                        {vcn.log_ocid.length > 25 ? `${vcn.log_ocid.substring(0, 25)}...` : vcn.log_ocid}
                                                      </code>
                                                    </TooltipTrigger>
                                                    <TooltipContent className="max-w-md">
                                                      <p className="text-xs font-mono break-all">{vcn.log_ocid}</p>
                                                    </TooltipContent>
                                                  </Tooltip>
                                                ) : (
                                                  <span className="text-muted-foreground text-sm">-</span>
                                                )}
                                              </TableCell>
                                              <TableCell>
                                                {vcn.last_poll_time ? (
                                                  <span className="text-xs text-muted-foreground">
                                                    {new Date(vcn.last_poll_time).toLocaleString()}
                                                  </span>
                                                ) : vcn.enabled ? (
                                                  <Tooltip>
                                                    <TooltipTrigger asChild>
                                                      <span className="text-xs text-muted-foreground cursor-help">
                                                        Initializing...
                                                      </span>
                                                    </TooltipTrigger>
                                                    <TooltipContent className="max-w-xs">
                                                      <p className="text-xs">Waiting for OCI to create log group. This can take a few minutes.</p>
                                                    </TooltipContent>
                                                  </Tooltip>
                                                ) : (
                                                  <span className="text-muted-foreground text-sm">-</span>
                                                )}
                                              </TableCell>
                                              <TableCell>
                                                {vcn.last_processed_timestamp ? (
                                                  <span className="text-xs text-muted-foreground">
                                                    {new Date(vcn.last_processed_timestamp).toLocaleString()}
                                                  </span>
                                                ) : vcn.enabled ? (
                                                  <Tooltip>
                                                    <TooltipTrigger asChild>
                                                      <span className="text-xs text-muted-foreground cursor-help">
                                                        Waiting for logs...
                                                      </span>
                                                    </TooltipTrigger>
                                                    <TooltipContent className="max-w-xs">
                                                      <p className="text-xs">No logs processed yet. OCI VCN Flow Logs can take a few minutes to start.</p>
                                                    </TooltipContent>
                                                  </Tooltip>
                                                ) : (
                                                  <span className="text-muted-foreground text-sm">-</span>
                                                )}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                      </div>

                                      {/* Pagination */}
                                      <PaginationControls
                                        page={currentPage}
                                        pageSize={VCN_PAGE_SIZE}
                                        totalItems={status.vcns.length}
                                        onPageChange={(newPage) => {
                                          setVcnPagination(prev => {
                                            const newMap = new Map(prev);
                                            newMap.set(connection.id, newPage);
                                            return newMap;
                                          });
                                        }}
                                        itemLabel="VCNs"
                                      />
                                    </>
                                  );
                                })()
                              ) : (
                                <div className="text-center py-6 text-muted-foreground">
                                  <p className="text-sm">No VCNs with resources discovered yet.</p>
                                  <p className="text-xs mt-1">VCNs with traffic-generating resources will appear here automatically within a few minutes.</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </CardContent>
          </Card>
          )}
        </div>
        {/* End OCI Parent Section */}
          </TabsContent>

          <TabsContent value="gcp" className="mt-4">
            <GCPCloudTab />
          </TabsContent>
        </Tabs>

        {/* AWS Connection Modal */}
        <Dialog open={showAWSModal} onOpenChange={(open) => !open && handleCloseAWSModal()}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {awsConnectionPurpose === 'vpc-flow-logs'
                  ? 'Connect AWS for VPC Flow Logs'
                  : awsConnectionPurpose === 'cloud-ingestion'
                  ? 'Connect AWS Account'
                  : 'Connect AWS Account'}
              </DialogTitle>
              <DialogDescription>
                {awsConnectionPurpose === 'vpc-flow-logs'
                  ? 'Set up secure access to your AWS account for VPC Flow Logs collection'
                  : awsConnectionPurpose === 'cloud-ingestion'
                  ? 'Set up secure role-based access to your AWS account for workflow automation'
                  : 'Set up secure access to your AWS account for EKS cluster management'}
              </DialogDescription>
            </DialogHeader>

            <Tabs value={awsModalStep} className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="configure" disabled={awsModalStep === 'verify'}>
                  1. Configure
                </TabsTrigger>
                <TabsTrigger value="verify" disabled={!awsBootstrapData}>
                  2. Verify
                </TabsTrigger>
              </TabsList>

              <TabsContent value="configure" className="space-y-4">
                {bootstrapError && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{bootstrapError}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-4">
                  <div>
                    <Label htmlFor="region">AWS Region</Label>
                    <Select value={selectedRegion} onValueChange={setSelectedRegion}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {awsRegions.map((region) => (
                          <SelectItem key={region.value} value={region.value}>
                            {region.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                    <Button variant="outline" onClick={handleCloseAWSModal}>
                      Cancel
                    </Button>
                    <Button
                      onClick={handleAWSBootstrap}
                      disabled={isBootstrapping}
                    >
                      {isBootstrapping ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Preparing...
                        </>
                      ) : (
                        <>
                          {awsConnectionPurpose === 'vpc-flow-logs'
                            ? 'Generate VPC Flow Logs Setup'
                            : awsConnectionPurpose === 'cloud-ingestion'
                            ? 'Generate Setup Instructions'
                            : 'Generate Setup Instructions'}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="verify" className="space-y-4">
                {/* Cloud Integration Flow - handles both SaaS and On-Prem */}
                {awsBootstrapData && (
                  <>
                    {verifyError && (
                      <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>{verifyError}</AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-4">
                      {/* Show different alerts for on-prem vs SaaS */}
                      {(awsBootstrapData as any).is_onprem ? (
                        <Alert>
                          <Server className="h-4 w-4" />
                          <AlertDescription>
                            <strong>On-Prem Mode:</strong> You'll need to enter your <strong>Pod Role Name</strong> in the CloudFormation console.
                            <br />
                            <span className="text-xs">This is the IAM role used by your Kestrel pods (IRSA role, Pod Identity role, or EKS node group role).</span>
                          </AlertDescription>
                        </Alert>
                      ) : (
                        <Alert>
                          <AlertDescription>
                            <strong>External ID:</strong> <code className="bg-muted px-1 py-0.5 rounded text-xs">{awsBootstrapData.external_id}</code>
                            <br />
                            Save this External ID - you'll need it for the CloudFormation stack.
                          </AlertDescription>
                        </Alert>
                      )}

                      <div className="space-y-4">
                        <h4 className="font-medium">Step 1: Deploy CloudFormation Stack</h4>
                        
                        <Tabs defaultValue="console" className="w-full">
                          <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="console">AWS Console</TabsTrigger>
                            <TabsTrigger value="cli">AWS CLI</TabsTrigger>
                          </TabsList>
                          
                          <TabsContent value="console" className="space-y-4">
                            <div className="space-y-3">
                              <Button
                                className={cn(
                                  "w-full justify-center h-auto p-4"
                                )}
                                onClick={() => window.open(awsBootstrapData.cfn_launch_url, '_blank')}
                              >
                                <div className="text-center">
                                    <div className="font-medium">
                                      {awsConnectionPurpose === 'vpc-flow-logs'
                                        ? 'Launch VPC Flow Logs Stack in AWS Console'
                                        : awsConnectionPurpose === 'cloud-ingestion'
                                        ? 'Launch Kestrel Stack in AWS Console'
                                        : 'Launch CloudFormation Stack in AWS Console'}
                                    </div>
                                    <div className={cn(
                                      "text-xs",
                                      "text-primary-foreground/70"
                                    )}>
                                      Opens in new tab with pre-filled parameters
                                    </div>
                                </div>
                              </Button>
                              
                              <div className="bg-muted/50 p-3 rounded-lg border">
                                <h5 className="font-medium mb-2">What happens next:</h5>
                                <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                                  <li>Click the button above to open AWS CloudFormation console</li>
                                  <li>Check "I acknowledge that AWS CloudFormation might create IAM resources"</li>
                                  <li>Click "Create stack" and wait for completion (~1 minute)</li>
                                  <li>Copy the Role ARN from the "Outputs" tab
                                    {awsConnectionPurpose === 'vpc-flow-logs' && (
                                      <span className="font-semibold"> (VPCFlowLogsRoleArn)</span>
                                    )}
                                    {awsConnectionPurpose === 'cloud-ingestion' && (
                                      <span className="font-semibold"> (IngestionRoleArn)</span>
                                    )}
                                  </li>
                                </ol>
                              </div>
                            </div>
                          </TabsContent>
                          
                          <TabsContent value="cli" className="space-y-4">
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">Create stack command:</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyToClipboard(awsBootstrapData.cli_create_cmd, 'cli-create')}
                                >
                                  <Copy className="h-3 w-3 mr-1" />
                                  {copySuccess === 'cli-create' ? 'Copied!' : 'Copy'}
                                </Button>
                              </div>
                              <div className="bg-muted p-3 rounded text-xs font-mono overflow-x-auto">
                                {awsBootstrapData.cli_create_cmd}
                              </div>
                              
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">Get Role ARN command:</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => copyToClipboard(awsBootstrapData.cli_output_cmd, 'cli-output')}
                                >
                                  <Copy className="h-3 w-3 mr-1" />
                                  {copySuccess === 'cli-output' ? 'Copied!' : 'Copy'}
                                </Button>
                              </div>
                              <div className="bg-muted p-3 rounded text-xs font-mono overflow-x-auto">
                                {awsBootstrapData.cli_output_cmd}
                              </div>
                            </div>
                          </TabsContent>
                        </Tabs>
                      </div>

                      <div className="space-y-3">
                        <h4 className="font-medium">Step 2: Enter Role ARN</h4>
                        <div>
                          <Label htmlFor="roleArn">IAM Role ARN</Label>
                          <Input
                            id="roleArn"
                            value={roleArn}
                            onChange={(e) => setRoleArn(e.target.value)}
                            placeholder={awsConnectionPurpose === 'vpc-flow-logs'
                              ? "arn:aws:iam::123456789012:role/Kestrel-VPCFlowLogsRole"
                              : awsConnectionPurpose === 'cloud-ingestion'
                              ? "arn:aws:iam::123456789012:role/Kestrel-CloudIngestionRole"
                              : "arn:aws:iam::123456789012:role/KestrelProvisioningRole"
                            }
                            className="font-mono text-sm"
                          />
                          <p className="text-xs text-muted-foreground mt-1">
                            Paste the Role ARN from your CloudFormation stack outputs
                          </p>
                        </div>
                      </div>

                      <div className="flex justify-end gap-2">
                        <Button variant="outline" onClick={handleCloseAWSModal}>
                          Cancel
                        </Button>
                        <Button 
                          onClick={handleAWSVerify} 
                          disabled={!roleArn.trim() || isVerifying}
                        >
                          {isVerifying ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Verifying...
                            </>
                          ) : (
                            <>
                              Verify & Connect
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>

        {/* AWS Connection Delete Confirmation Dialog */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete AWS Connection</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete all connections to AWS account{' '}
                <code className="bg-muted px-1 py-0.5 rounded text-xs">
                  {connectionToDelete?.account_id}
                </code>?
                This will remove all cloud resources, VPC flow logs, and access to this account.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteConnection}
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Connection'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* OCI Connection Modal */}
        <Dialog open={showOCIModal} onOpenChange={(open) => !open && handleCloseOCIModal()}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="pr-8">
                Connect Oracle Cloud Infrastructure
              </DialogTitle>
              <DialogDescription>
                Set up secure API key access to your OCI tenancy for cloud resource discovery
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {ociError && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{ociError}</AlertDescription>
                </Alert>
              )}

              {/* Setup Instructions */}
              {ociSetupSteps.length > 0 && (
                <div className="bg-muted/50 p-4 rounded-lg border">
                  <h4 className="font-medium mb-3">Setup Steps:</h4>
                  <div className="space-y-3">
                    {ociSetupSteps.map((step) => (
                      <div key={step.step_number} className="flex gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-foreground text-background flex items-center justify-center text-xs font-medium">
                          {step.step_number}
                        </div>
                        <div className="flex-grow">
                          <p className="font-medium text-sm">{step.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
                          {step.command && (
                            <div className="flex items-center gap-2 mt-2">
                              <code className="bg-muted px-2 py-1 rounded text-xs font-mono flex-grow overflow-x-auto">
                                {step.command}
                              </code>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => copyOCIToClipboard(step.command!, `step-${step.step_number}`)}
                              >
                                <Copy className="h-3 w-3" />
                                {ociCopySuccess === `step-${step.step_number}` ? 'Copied!' : ''}
                              </Button>
                            </div>
                          )}
                          {step.link && (
                            <a
                              href={step.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-muted-foreground hover:underline flex items-center gap-1 mt-1"
                            >
                              <ExternalLink className="h-3 w-3" />
                              Documentation
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Credentials Form */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="oci-tenancy">Tenancy OCID *</Label>
                    <Input
                      id="oci-tenancy"
                      value={ociForm.tenancy_ocid}
                      onChange={(e) => setOciForm(prev => ({ ...prev, tenancy_ocid: e.target.value }))}
                      placeholder="ocid1.tenancy.oc1.."
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="oci-user">User OCID *</Label>
                    <Input
                      id="oci-user"
                      value={ociForm.user_ocid}
                      onChange={(e) => setOciForm(prev => ({ ...prev, user_ocid: e.target.value }))}
                      placeholder="ocid1.user.oc1.."
                      className="font-mono text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="oci-fingerprint">API Key Fingerprint *</Label>
                    <Input
                      id="oci-fingerprint"
                      value={ociForm.fingerprint}
                      onChange={(e) => setOciForm(prev => ({ ...prev, fingerprint: e.target.value }))}
                      placeholder="aa:bb:cc:dd:ee:ff:00:11:22:33:44:55:66:77:88:99"
                      className="font-mono text-sm"
                    />
                  </div>
                  <div>
                    <Label htmlFor="oci-region">Region *</Label>
                    <Select 
                      value={ociForm.region} 
                      onValueChange={(value) => setOciForm(prev => ({ ...prev, region: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ociRegions.length > 0 ? (
                          ociRegions.map((region) => (
                            <SelectItem key={region.identifier} value={region.identifier}>
                              {region.name}
                            </SelectItem>
                          ))
                        ) : (
                          <>
                            <SelectItem value="us-ashburn-1">US East (Ashburn)</SelectItem>
                            <SelectItem value="us-phoenix-1">US West (Phoenix)</SelectItem>
                            <SelectItem value="eu-frankfurt-1">Germany Central (Frankfurt)</SelectItem>
                            <SelectItem value="uk-london-1">UK South (London)</SelectItem>
                            <SelectItem value="ap-tokyo-1">Japan East (Tokyo)</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="oci-private-key">Private Key (PEM format) *</Label>
                  <textarea
                    id="oci-private-key"
                    value={ociForm.private_key}
                    onChange={(e) => setOciForm(prev => ({ ...prev, private_key: e.target.value }))}
                    placeholder="-----BEGIN RSA PRIVATE KEY-----&#10;...&#10;-----END RSA PRIVATE KEY-----"
                    className="w-full h-32 px-3 py-2 text-sm font-mono border rounded-md bg-background resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Paste the contents of your API signing key private key file
                  </p>
                </div>

                <div>
                  <Label htmlFor="oci-name">Connection Name (optional)</Label>
                  <Input
                    id="oci-name"
                    value={ociForm.connection_name}
                    onChange={(e) => setOciForm(prev => ({ ...prev, connection_name: e.target.value }))}
                    placeholder="My OCI Account"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    A friendly name to identify this connection
                  </p>
                </div>
              </div>

              <Alert>
                <AlertDescription className="text-sm">
                  <strong>Security Note:</strong> Your private key is securely stored using encryption
                  and is never exposed in the UI. It's only used to authenticate API requests to OCI.
                </AlertDescription>
              </Alert>

              <Alert>
                <Terminal className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  <strong>Want to apply OCI CLI fixes from Kestrel?</strong>
                  <p className="mt-1">
                    To execute OCI CLI commands directly from Kestrel's AI chat interface, your IAM policy needs 
                    <code className="mx-1 px-1 py-0.5 bg-muted rounded text-xs">manage all-resources</code>
                    permission instead of just <code className="mx-1 px-1 py-0.5 bg-muted rounded text-xs">read all-resources</code>.
                  </p>
                </AlertDescription>
              </Alert>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleCloseOCIModal}>
                Cancel
              </Button>
              <Button 
                onClick={handleOCIVerify} 
                disabled={!ociForm.tenancy_ocid.trim() || !ociForm.user_ocid.trim() || 
                         !ociForm.fingerprint.trim() || !ociForm.private_key.trim() || isOCIVerifying}
              >
                {isOCIVerifying ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify & Connect
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* OCI Connection Delete Confirmation Dialog */}
        <Dialog open={showOCIDeleteDialog} onOpenChange={setShowOCIDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete OCI Connection</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete the connection to OCI tenancy{' '}
                <code className="bg-muted px-1 py-0.5 rounded text-xs">
                  {ociConnectionToDelete?.connection_name || 
                   ociConnectionToDelete?.tenancy_ocid.split('.').pop()?.substring(0, 12) + '...'}
                </code>?
                This will remove access to manage resources in this tenancy.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowOCIDeleteDialog(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteOCIConnection}
                disabled={isOCIDeleting}
              >
                {isOCIDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete Connection'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
